import type {ReactElement} from 'react';
import {useEffect, useState} from 'react';
import {Text, TextSize} from '@Components/text';
import {InputField} from '@Components/input-field-v2';
import {Dropdown} from '@Components/dropdown-v2';
import useMergeTags from '@Hooks/merge-tags/useMergeTags';
import type {MergeTag} from '@Components/merge-tags/merge-tags.types';
import {capitalizeFirstLetter} from '@Utils/string.util';
import type {MergeTagsSmartPreviewDropdownProps} from './merge-tags-smart-preview-dropdown.types';
import styles from './merge-tags-smart-preview-dropdown.module.scss';

export function MergeTagsSmartPreviewDropdown({...props}: MergeTagsSmartPreviewDropdownProps): ReactElement<any> | null {
  const [open, setOpen] = useState<boolean>(false);
  const {mergeTagOptions, isLoading} = useMergeTags();
  const [options, setOptions] = useState<MergeTag[]>([]);
  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const [isEmpty, setIsEmpty] = useState<boolean>(false);
  const filterableMergeTags = Array.isArray(props.tagsInCampaign) ? props.tagsInCampaign : Object.values(props.tagsInCampaign);

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const CustomTrigger = props.triggerButton.type;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const CustomConfirmationButton = props.confirmationButton.type;

  useEffect(() => {
    if (isLoading) {
      return;
    }

    fetchMergeTagsInCampaign();
  }, [isLoading]);

  const fetchMergeTagsInCampaign = (): void => {
    setIsEmpty(false);

    const previewOptions = mergeTagOptions.filter((tag) => {
      return filterableMergeTags.includes(capitalizeFirstLetter(tag.mergeTagKey));
    });

    if (!previewOptions.length) {
      setIsEmpty(true);
      return;
    }

    const initialValues: Record<string, string> = {};
    previewOptions.forEach((tag) => {
      initialValues[tag.mergeTagKey] = '';
    });

    setOptions(previewOptions);
    setInputValues(initialValues);
  };

  const handleInputChange = (key: string, value: string): void => {
    setInputValues((prev) => {
      return {
        ...prev,
        [key]: value,
      };
    });
  };

  const handleConfirmation = (): void => {
    props.onConfirmed(inputValues);
  };

  const getPopup = (): ReactElement<any> => {
    return (
      <div className="spacing-p-2">
        <div className="flexbox flex-column">
          <Text val="Smart Preview" size={TextSize.XSMALL} bold />
          <Text val="Input sample data to see to see what your email will look like." size={TextSize.XXSMALL} />
        </div>
        <hr className={styles.separator} />
        <div>
          {options.map((tag) => {
            return (
              <InputField
                key={tag.mergeTagKey}
                label={window.i18next.t(tag.label)}
                placeholder={window.i18next.t(tag.label)}
                input={inputValues[tag.mergeTagKey]}
                onInputChange={(value) => {
                  handleInputChange(tag.mergeTagKey, value as string);
                }}
                className="spacing-m-b-3"
              />
            );
          })}
        </div>
        <CustomConfirmationButton key={props.confirmationButton.key} {...props.confirmationButton.props} onClick={handleConfirmation} />
      </div>
    );
  };

  const onClose = (): void => {
    setOpen(false);
  };

  const onOpen = (): void => {
    setOpen(true);
  };

  const getTrigger = (): ReactElement<any> => {
    return <CustomTrigger {...props.triggerButton.props} isActive={open} {...(isEmpty && {onClick: handleConfirmation})} />;
  };

  if (isLoading) {
    return null;
  }

  if (isEmpty) {
    return getTrigger();
  }

  return (
    <Dropdown
      onOpen={onOpen}
      onClose={onClose}
      popupOffset={10}
      selector={getTrigger()}
      popup={getPopup()}
      popupClassName={`${styles.smartPreviewPopup} border-s-dark radius-8`}
      modalTitle={window.i18next.t('pmwjs_merge_tags')}
      doCloseOnInnerClick={false}
      shouldShift
      shouldFlip
      popUpHasCustomWidth
      popUpHasCustomHeight
      doesCloseOnOuterClick
      showModalForSmallScreen
      shouldCenterAlignModalTitle
    />
  );
}
